import * as React from 'react';
import { atom, createStore, Provider, useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { noop, pick } from 'lodash';
import { produce, type Draft } from 'immer';
import { Form, type FormInstance } from 'antd';
import { useDimensions, useCriticalEvents } from '@src/services/queries';

type DashboardData = Omit<
  AnalysisDashboard,
  'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'critical_events_detail'
>;

export interface DashboardProviderProps<T extends DashboardData = DashboardData> {
  data: T;
  onChange?: (id: T['id'], value: T['config']) => void;
  onSave?: (id: T['id'], value: T['config']) => void;
  onShare?: (id: T['id'], value: T['config']) => void;
  children?: React.ReactNode;
}

export interface DashboardSettingValue
  extends Pick<
    DashboardProviderProps['data']['config'],
    'critical_events' | 'timeout' | 'propagation_config'
  > {}

interface DashboardActions {
  onChange: (
    value: DashboardData['config'] | ((draft: Draft<DashboardData['config']>) => void),
  ) => void;
  onSave: (id: string) => void;
  onShare: (id: string) => void;
}

function getReadonlyPartial<T = AnalysisDashboard>(config: T) {
  return pick(config, [
    'id',
    'type',
    'analysis',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
  ]) as ReadonlyPick<T>;
}

// From Props
const dashboardAtom = atom({} as DashboardProviderProps['data']);
const actionsAtom = atom<DashboardActions>({
  onChange: noop as any,
  onSave: noop as any,
  onShare: noop as any,
});

// Internal UI state
export const activeTabAtom = atom<'breakdown' | 'dropoff'>('breakdown');
export const dropOffIndexAtom = atom<number>(1);

// Settings Form instance atom
export const formInstanceAtom = atom<FormInstance<DashboardSettingValue>>();

// Bridge atoms
const dimensionsAtom = atom<ReturnType<typeof useDimensions>['data']>();
const criticalEventsAtom = atom<ReturnType<typeof useCriticalEvents>['data']>();

// Generate finer-grained atoms to avoid unnecessary re-render
// States
const dashboardConfigAtom = selectAtom(dashboardAtom, (s) => s.config);
const dashboardReadonlyDataAtom = selectAtom(dashboardAtom, (s) => getReadonlyPartial(s));
const dashboardNameAtom = selectAtom(dashboardConfigAtom, (s) => s.name);
const dashboardRawPatternAtom = selectAtom(dashboardConfigAtom, (s) => s.critical_events);
const dashboardPatternAtom = atom((get) => {
  const criticalEvents = get(criticalEventsAtom);
  const rawPattern = get(dashboardRawPatternAtom);

  return rawPattern.filter((item) => criticalEvents?.criticalEventMap.has(item));
});
const dashboardDurationLimitAtom = selectAtom(dashboardConfigAtom, (s) => s.timeout);
const dashboardPropagationSettingsAtom = selectAtom(
  dashboardConfigAtom,
  (s) => s.propagation_config,
);
const dashboardRawFiltersAtom = selectAtom(dashboardConfigAtom, (s) => s.filters);
const dashboardFiltersAtom = atom((get) => {
  const dimensions = get(dimensionsAtom);
  const rawFilters = get(dashboardRawFiltersAtom);

  return rawFilters
    ?.map((group) => group.filter((rules) => dimensions?.dimensionMap.has(rules.field)))
    .filter((group) => group.length > 0);
});
const dashboardIntervalAtom = selectAtom(dashboardConfigAtom, (s) => s.time_range);
const dashboardFunnelAggregationAtom = selectAtom(
  dashboardConfigAtom,
  (s) => s.funnel_select_aggregation,
);
const dashboardDimensionTablesAtom = selectAtom(dashboardConfigAtom, (s) => s.dimension_analyses);

// Actions
const dashboardOnChangeAtom = selectAtom(actionsAtom, (s) => s.onChange);
const dashboardOnSaveAtom = selectAtom(actionsAtom, (s) => s.onSave);
const dashboardOnShareAtom = selectAtom(actionsAtom, (s) => s.onShare);

// Derived states for internal subscriptions
const dimensionsUsedAtom = atom((get) => {
  const dimensions = get(dimensionsAtom);
  const tables = get(dashboardDimensionTablesAtom) ?? [];
  const filters = get(dashboardFiltersAtom) ?? [];

  return new Set(
    [
      ...tables.map(({ dimension }) => dimension),
      ...filters.flat(2).map(({ field }) => field),
    ].filter((item) => dimensions?.dimensionMap.has(item)),
  );
});

export const DashboardProvider = (props: DashboardProviderProps) => {
  const { data, children, onChange, onSave, onShare } = props;
  const { data: dimensions } = useDimensions();
  const { data: criticalEvents } = useCriticalEvents();
  const [formInstance] = Form.useForm<DashboardSettingValue>();
  const store = React.useRef<ReturnType<typeof createStore>>();
  const onChangeRef = React.useRef(onChange);
  const onSaveRef = React.useRef(onSave);
  const onShareRef = React.useRef(onShare);

  // Always keep the latest callback
  onChangeRef.current = onChange;
  onSaveRef.current = onSave;
  onShareRef.current = onShare;

  const updateDimensionAnalysesOrderBy = React.useCallback(() => {
    if (!store.current) return;

    store.current.set(dashboardAtom, (prev) =>
      produce(prev, (draft) => {
        const pattern = store.current!.get(dashboardPatternAtom);

        draft.config.dimension_analyses.forEach((item) => {
          if (typeof item.order_by?.step === 'number' && item.order_by.step > pattern.length) {
            item.order_by.step = pattern.length > 0 ? pattern.length : 'full';
          }
        });
      }),
    );
  }, []);

  const updatePropagationConfig = React.useCallback(() => {
    if (!store.current) return;

    store.current.set(dashboardAtom, (prev) =>
      produce(prev, (draft) => {
        const currentDimensions = store.current!.get(dimensionsUsedAtom);
        const dimensionMap = store.current!.get(dimensionsAtom)?.dimensionMap;

        if (!draft.config.propagation_config) {
          draft.config.propagation_config = {};
        }

        Object.keys(draft.config.propagation_config).forEach((dimension) => {
          if (!currentDimensions.has(dimension)) {
            delete draft.config.propagation_config[dimension];
          }
        });

        currentDimensions.forEach((dimension) => {
          if (!(dimension in draft.config.propagation_config)) {
            const dimensionMeta = dimensionMap?.get(dimension);
            draft.config.propagation_config[dimension] = dimensionMeta?.predefined
              ? 'global'
              : 'context';
          }
        });
      }),
    );
  }, []);

  if (!store.current) {
    // Create store and initialize atoms
    store.current = createStore();
    store.current.set(actionsAtom, {
      onChange: (value) => {
        store.current!.set(dashboardAtom, (prev) => {
          return {
            ...prev,
            config: typeof value === 'function' ? produce(prev.config, value) : value,
          };
        });
      },
      onSave: (id: string) => onSaveRef.current?.(id, store.current!.get(dashboardConfigAtom)),
      onShare: (id: string) => onShareRef.current?.(id, store.current!.get(dashboardConfigAtom)),
    });

    store.current.set(activeTabAtom, 'breakdown');
    store.current.set(dropOffIndexAtom, 1);
    store.current.set(formInstanceAtom, formInstance);
    store.current.set(dimensionsAtom, dimensions);
    store.current.set(criticalEventsAtom, criticalEvents);

    // Initialize dashboard data
    store.current.set(dashboardAtom, data);
    updateDimensionAnalysesOrderBy();
    updatePropagationConfig();
  }

  // Sync dimensions and critical events data to bridge atoms
  React.useEffect(() => {
    store.current!.set(dimensionsAtom, dimensions);
  }, [dimensions]);

  React.useEffect(() => {
    store.current!.set(criticalEventsAtom, criticalEvents);
  }, [criticalEvents]);

  React.useEffect(() => {
    const unsubs = [
      store.current!.sub(dashboardConfigAtom, () => {
        onChangeRef.current?.(
          store.current!.get(dashboardReadonlyDataAtom).id,
          store.current!.get(dashboardConfigAtom),
        );
      }),

      store.current!.sub(dashboardPatternAtom, () => {
        updateDimensionAnalysesOrderBy();

        if (
          store.current!.get(dropOffIndexAtom) >
          store.current!.get(dashboardPatternAtom).length - 1
        ) {
          store.current!.set(dropOffIndexAtom, 1);
        }
      }),

      store.current!.sub(dimensionsUsedAtom, () => {
        updatePropagationConfig();
      }),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [updateDimensionAnalysesOrderBy, updatePropagationConfig]);

  return <Provider store={store.current}>{children}</Provider>;
};

export const useDashboardConfig = () => useAtomValue(dashboardConfigAtom);
export const useDashboardReadonlyData = () => useAtomValue(dashboardReadonlyDataAtom);
export const useDashboardName = () => useAtomValue(dashboardNameAtom);
export const useDashboardRawPattern = () => useAtomValue(dashboardRawPatternAtom);
export const useDashboardPattern = () => useAtomValue(dashboardPatternAtom);
export const useDashboardDurationLimit = () => useAtomValue(dashboardDurationLimitAtom);
export const useDashboardPropagationSettings = () => useAtomValue(dashboardPropagationSettingsAtom);
export const useDashboardRawFilters = () => useAtomValue(dashboardRawFiltersAtom);
export const useDashboardFilters = () => useAtomValue(dashboardFiltersAtom);
export const useDashboardInterval = () => useAtomValue(dashboardIntervalAtom);
export const useDashboardFunnelAggregation = () => useAtomValue(dashboardFunnelAggregationAtom);
export const useDashboardDimensionTables = () => useAtomValue(dashboardDimensionTablesAtom);

export const useOnChange = () => useAtomValue(dashboardOnChangeAtom);
export const useOnSave = () => useAtomValue(dashboardOnSaveAtom);
export const useOnShare = () => useAtomValue(dashboardOnShareAtom);
